/* Admin shell — top bar + side rail. Counts come from Firestore hooks; user chip
   and sign-out use Firebase Auth. */
import { useEffect, useRef, useState } from "react";
import { signOut } from "firebase/auth";
import { auth } from "../../lib/firebase.js";
import { Link, useRoute, navigate } from "../../lib/router-compat.jsx";
import { Icon, BrandMark } from "../../ui/index.js";
import { useAuth } from "../../auth/AuthProvider.jsx";
import { useLeads, useEnquiries, usePosts, useEvents, useSchoolGroups } from "../../data/hooks.js";

export default function AdminShell({ children }) {
  const route = useRoute();
  const { profile } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const { leads } = useLeads();
  const { enquiries } = useEnquiries();
  const { posts } = usePosts();
  const { events } = useEvents();
  const { groups } = useSchoolGroups();

  const counts = {
    "/admin/posts": posts.filter(p => p.status === "draft").length,
    "/admin/leads": leads.filter(l => l.status === "new").length,
    "/admin/enquiries": enquiries.filter(e => e.status === "new").length,
    "/admin/events": events.filter(e => e.status === "upcoming").length,
    "/admin/groups": groups.filter(g => g.status === "pending").length,
  };

  const nav = [
    { label: "Dashboard", path: "/admin", icon: Icon.Home, exact: true },
    { label: "Events", path: "/admin/events", icon: Icon.Calendar },
    { label: "Posts", path: "/admin/posts", icon: Icon.FileText },
    { label: "Pages", path: "/admin/pages", icon: Icon.Layout },
    { label: "Programs", path: "/admin/programs", icon: Icon.Star },
    { label: "Gallery", path: "/admin/gallery", icon: Icon.Images },
    { label: "School groups", path: "/admin/groups", icon: Icon.School },
    { label: "Donor leads", path: "/admin/leads", icon: Icon.Users },
    { label: "Enquiries", path: "/admin/enquiries", icon: Icon.Inbox },
    { label: "Donations", path: "/admin/donations", icon: Icon.Gift },
    { label: "Settings", path: "/admin/settings", icon: Icon.Settings },
  ];

  const isActive = (item) =>
    item.exact ? route.path === item.path : route.path === item.path || route.path.startsWith(item.path + "/");
  const currentItem = nav.find(isActive);
  const initials = profile?.initials || (profile?.displayName || "Admin").split(" ").map(w => w[0]).slice(0, 2).join("");

  useEffect(() => { setDrawerOpen(false); setUserMenuOpen(false); }, [route.path]);
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  useEffect(() => {
    if (!userMenuOpen) return;
    const onPointer = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setUserMenuOpen(false); };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [userMenuOpen]);

  const handleSignOut = async () => {
    await signOut(auth);
    navigate("/admin/sign-in", { replace: true });
  };

  return (
    <div className="admin-shell admin">
      <header className="admin-topbar">
        <button className="admin-topbar__hamburger" aria-label="Open menu" onClick={() => setDrawerOpen(true)}>
          <Icon.Menu size={20} />
        </button>
        <Link to="/admin" className="admin-topbar__logo">
          <BrandMark size={28} />
          <span className="admin-topbar__logo-text">Purple Angels · Admin</span>
        </Link>
        <div className="admin-topbar__page-title">{currentItem?.label || "Admin"}</div>
        <div className="admin-topbar__user" ref={userMenuRef}>
          <button
            type="button"
            className="admin-topbar__user-chip"
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
            aria-label="Account menu"
            onClick={() => setUserMenuOpen(o => !o)}
          >
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--primary)", color: "white", display: "grid", placeItems: "center", fontSize: 11, fontWeight: 600 }}>
              {initials}
            </div>
            <span className="admin-topbar__user-name">{profile?.displayName || "Admin"}</span>
            <Icon.ChevronDown size={14} style={{ color: "var(--text-muted)" }} />
          </button>
          {userMenuOpen && (
            <div className="admin-topbar__user-menu" role="menu">
              <div className="admin-topbar__user-menu-head">
                <span className="admin-topbar__user-menu-name">{profile?.displayName || "Admin"}</span>
                {profile?.email && <span className="admin-topbar__user-menu-email">{profile.email}</span>}
              </div>
              <Link to="/" className="admin-topbar__user-menu-item" role="menuitem">
                <Icon.ExternalLink size={16} />
                <span>View public site</span>
              </Link>
              <button type="button" className="admin-topbar__user-menu-item" role="menuitem" onClick={handleSignOut}>
                <Icon.LogOut size={16} />
                <span>Sign out</span>
              </button>
            </div>
          )}
        </div>
      </header>
      <div className="admin-body">
        <aside className={`admin-rail ${drawerOpen ? "admin-rail--open" : ""}`} aria-label="Admin navigation">
          <button className="admin-rail__close" aria-label="Close menu" onClick={() => setDrawerOpen(false)}>
            <Icon.X size={18} />
          </button>
          {nav.map(item => {
            const Ico = item.icon;
            const active = isActive(item);
            const cnt = counts[item.path] || 0;
            return (
              <Link key={item.path} to={item.path} className={`admin-rail__item ${active ? "active" : ""}`}>
                <Ico size={16} />
                <span>{item.label}</span>
                {cnt > 0 && <span className="admin-rail__count">{cnt}</span>}
              </Link>
            );
          })}
          <div style={{ marginTop: "auto", paddingTop: 16, borderTop: "1px solid var(--border)" }}>
            <Link to="/" className="admin-rail__item">
              <Icon.ExternalLink size={16} />
              <span>View public site</span>
            </Link>
            <button className="admin-rail__item" onClick={handleSignOut} style={{ width: "100%", background: "none", border: "none", cursor: "pointer", font: "inherit", textAlign: "left" }}>
              <Icon.LogOut size={16} />
              <span>Sign out</span>
            </button>
          </div>
        </aside>
        {drawerOpen && <div className="admin-rail__backdrop" onClick={() => setDrawerOpen(false)} />}
        <div className="admin-main">{children}</div>
      </div>
    </div>
  );
}
